import { getActionContext } from 'cadenza/action-context';
import {
  getSelectedLayerFeatures,
  getSelectedLayerFeaturesByGeometry
} from 'cadenza/api-client/maps-api';
import { context } from 'cadenza/map/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import { logger } from 'cadenza/utils/logging';
import h from 'hyperscript';
import Point from 'ol/geom/Point';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import { openMenu } from 'ui/legacy-menu/flyout-menu';

import i18n from './interactions.properties';

export class SelectContextMenu {

  #map;
  #interaction;
  #requestDelay;
  #scheduledRequest;

  constructor (map, interaction) {
    this.#map = map;
    this.#interaction = interaction;
    this.#requestDelay = 150;
  }

  prepareToShowContextMenu (mapBrowserEvent) {
    if (mapBrowserEvent.type === MapBrowserEventType.POINTERMOVE) {
      this._makeRequestWithDelay(mapBrowserEvent, this.#requestDelay);
    }
  }

  showContextMenuIfSelectedFeaturesExist (mapBrowserEvent) {
    this._makeRequestForSelectedFeatures(mapBrowserEvent);
  }

  showContextMenu (mapBrowserEvent, features) {
    clearTimeout(this.#scheduledRequest);
    const layer = this.#map.get(MapProperty.SELECTED_LAYER);
    const heading = h('span.d-dropdown-heading', i18n('select-context-menu.itemSelected', { count: features.length }));

    const actionProvider = this.#map.get(MapProperty.LAYER_ACTIONS_PROVIDER);
    const allActionsIsSupportedPromises = Object.entries(actionProvider(layer))
      .map(([ actionType, action ]) =>
        action.isSupported(getActionContext(this.#map))
          .then(isSupported => ({ isSupported, action, actionType })));

    Promise.all(allActionsIsSupportedPromises).then(allActionsIsSupported => {
      const contextMenuActionBtns = allActionsIsSupported
        .filter(item => item.isSupported && item.action.requiresObjectSelection())
        .map(item => this._baseContextMenuButton(item.actionType, item.action));
      if (contextMenuActionBtns.length) {
        openMenu(this._retrieveOriginalEvent(mapBrowserEvent), [ heading, ...contextMenuActionBtns ], {
          eventAsTarget: true,
          closeMenuEvents: MapBrowserEventType.POINTERMOVE
        });
      }
    }).catch(error => {
      logger.error('Error opening selection menu', error);
    });
  }

  _retrieveOriginalEvent (mapBrowserEvent) {
    if (mapBrowserEvent.originalEvent) {
      return mapBrowserEvent.originalEvent;
    }
    if (mapBrowserEvent.mapBrowserEvent) {
      return mapBrowserEvent.mapBrowserEvent.originalEvent;
    }
    return mapBrowserEvent;
  }

  _makeRequestWithDelay (mapBrowserEvent, delay) {
    clearTimeout(this.#scheduledRequest);
    this.#scheduledRequest = setTimeout(() => this._makeRequestForPosition(mapBrowserEvent), delay);
  }

  _makeRequestForPosition (mapBrowserEvent) {
    const geometry = new Point(mapBrowserEvent.coordinate);
    const map = this.#map;
    const layer = map.get(MapProperty.SELECTED_LAYER);
    const selectedLayerId = layer.get(LayerProperty.ID);
    const mapSize = map.getSize();
    const mapExtent = map.getView().calculateFullExtent();
    const srsCode = map.getView().getProjection().getCode();
    getSelectedLayerFeaturesByGeometry(selectedLayerId, mapSize, mapExtent, geometry, srsCode, context(map).mapScale)
      .then(({ features }) => {
        if (features.length) {
          this._makeRequestForSelectedFeatures(mapBrowserEvent);
        }
      })
      .catch(error => this._handleError(error));
  }

  _makeRequestForSelectedFeatures (mapBrowserEvent) {
    const map = this.#map;
    const layer = map.get(MapProperty.SELECTED_LAYER);
    const selectedLayerId = layer.get(LayerProperty.ID);
    const srsCode = map.getView().getProjection().getCode();
    getSelectedLayerFeatures(selectedLayerId, srsCode, context(map).mapScale)
      .then(({ features }) => {
        if (features.length) {
          this.showContextMenu(mapBrowserEvent, features);
        }
      })
      .catch(error => this._handleError(error));
  }

  _handleError (error) {
    if (error) {
      logger.error('Failed to show context menu', error);
    }
  }

  _baseContextMenuButton (id, action) {
    const button = h('button.button.button-borderless', {
      type: 'button',
      id: `context-action--${id}`,
      disabled: true,
      onclick: () => {
        action
          .execute()
          .catch(reason => logger.error('Cannot execute action', reason));
      }
    }, [ action.label ]);
    const onChangeSupported = () => {
      action.isSupported(getActionContext(this.#map))
        .then(supported => { button.disabled = !supported; })
        .catch(() => { button.disabled = true; });
    };
    onChangeSupported();
    action.on('change:supported', onChangeSupported);

    return button;
  }

}
